import { EaiwsSession } from '@easterngraphics/wcf/modules/eaiws';
import { fetchJson } from '@easterngraphics/wcf/modules/utils/async';
import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

interface GatekeeperResponse {
    server: string;
    sessionId: string;
    keepAliveInterval: number;
}

interface GatekeeperErrorResponse {
    error?: GatekeeperErrorDetails;
}

interface GatekeeperErrorDetails {
    id: string;
    type: string;
    code: number;
    scopes: Array<string>;
    message: string;
}

export interface GatekeeperOptions {
    /** pCon.login access token */
    userToken?: string;
    /** application specific id  */
    applicationId?: string;
    /** Can be used to predefined the locale of the created session */
    locale?: string;
}

/**
 * Request an EAIWS session from the Gatekeeper with fallback handling.
 * @param session EAIWS session object
 * @param gatekeeperId The gatekeeper id which should be used
 * @param options Additional options
 */
export async function getSessionFromGatekeeper(session: EaiwsSession,
    gatekeeperId: string, options: GatekeeperOptions): Promise<void> {
    try {
        await requestSession(session, gatekeeperId, options, false);
    } catch (error) {
        console.log('Failed to start gatekeeper session: ' + error);
        console.log('Using fallback gatekeeper request.');
        // Because the gatekeeper uses load balancing with multiple EAIWS servers,
        // the fallback request will most likely use a different EAIWS server.
        return (requestSession(session, gatekeeperId, options, true));
    }
}

/**
 * Helper function to request an EAIWS session from the Gatekeeper server.
 */
async function requestSession(session: EaiwsSession, gatekeeperId: string,
    options: GatekeeperOptions, asFallback: boolean): Promise<void> {
    const response = await fetchJson<GatekeeperResponse>(
        'https://gatekeeper.eaiws.pcon-solutions.com/v3/session/' + gatekeeperId,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(options),
            retryAttempts: 0,
            timeout: asFallback ? 10000 : 60000, // wait longer if this is the fallback request
            onGetResponseError: getGatekeeperErrorFromResponse // improve error message
        }
    );

    // connect to the received session
    session.connect(response.server, response.sessionId, response.keepAliveInterval * 1000);
}

async function getGatekeeperErrorFromResponse(response: Response): Promise<Error | null> {
    let errorResponse: GatekeeperErrorResponse | undefined;
    try {
        errorResponse = await response.json() as GatekeeperErrorResponse | undefined;
    } catch {
        return (null); // use default error message
    }

    if (errorResponse != null && errorResponse.error != null) {
        let message = errorResponse.error.id;
        if (errorResponse.error.scopes != null && errorResponse.error.scopes.length > 0) {
            message += '(' + errorResponse.error.scopes[0] + ')';
        }
        if (isNotNullOrEmpty(errorResponse.error.message)) {
            message += ' - ' + errorResponse.error.message;
        }
        return (new Error('[' + response.status + '] ' + message));
    }

    return (null); // use default error message
};
